/**
 * FileUpload Component
 *
 * Drag-and-drop file upload zone with progress tracking.
 */

import * as React from 'react';
import { useCallback, useState, useRef } from 'react';
import { Upload, X, File, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { useFileUpload, UploadProgress } from '@/lib/hooks/useFileUpload';
import { api, FileCategory, UserFileResponse } from '@/lib/api';

export interface FileUploadProps {
  /** Callback when upload completes */
  onUploadComplete?: (files: UserFileResponse[]) => void;
  /** Callback when upload fails */
  onUploadError?: (fileName: string, error: Error) => void;
  /** Callback for progress updates */
  onProgress?: (progress: UploadProgress[]) => void;
  /** Accepted MIME types (e.g., "image/*,application/pdf") */
  accept?: string;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Maximum number of files */
  maxFiles?: number;
  /** Target file category */
  category?: FileCategory;
  /** Target folder ID */
  folderId?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether uploads are disabled */
  disabled?: boolean;
}

/**
 * File upload component with drag-and-drop support
 */
interface DuplicateInfo {
  file: File;
  existingFile: UserFileResponse;
}

export function FileUpload({
  onUploadComplete,
  onUploadError,
  onProgress,
  accept,
  maxSize = 500 * 1024 * 1024, // 500MB default
  maxFiles = 10,
  category = 'general',
  folderId,
  className,
  disabled = false,
}: FileUploadProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [duplicateDialog, setDuplicateDialog] = useState<{
    duplicates: DuplicateInfo[];
    nonDuplicates: File[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const completedFilesRef = useRef<UserFileResponse[]>([]);

  const {
    uploads,
    isUploading,
    uploadFiles,
    cancelUpload,
    clearCompleted,
  } = useFileUpload({
    defaultCategory: category,
    defaultFolderId: folderId,
    onUploadComplete: (file) => {
      completedFilesRef.current.push(file);
      // Check if all uploads are done
      const stillUploading = uploads.some(
        (u) => u.status === 'uploading' || u.status === 'processing' || u.status === 'pending'
      );
      if (!stillUploading && completedFilesRef.current.length > 0) {
        onUploadComplete?.(completedFilesRef.current);
        completedFilesRef.current = [];
      }
    },
    onUploadError,
    onProgress,
  });

  const validateFiles = useCallback((files: File[]): File[] => {
    setValidationError(null);
    const validFiles: File[] = [];

    if (files.length > maxFiles) {
      setValidationError(`Maximum ${maxFiles} files allowed`);
      return [];
    }

    for (const file of files) {
      // Check size
      if (file.size > maxSize) {
        setValidationError(`File "${file.name}" exceeds maximum size of ${formatBytes(maxSize)}`);
        continue;
      }

      // Check type if accept is specified
      if (accept) {
        const acceptedTypes = accept.split(',').map((t) => t.trim());
        const fileType = file.type;
        const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;

        const isAccepted = acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith('.')) {
            return fileExt === acceptedType.toLowerCase();
          }
          if (acceptedType.endsWith('/*')) {
            const baseType = acceptedType.slice(0, -2);
            return fileType.startsWith(baseType);
          }
          return fileType === acceptedType;
        });

        if (!isAccepted) {
          setValidationError(`File "${file.name}" is not an accepted file type`);
          continue;
        }
      }

      validFiles.push(file);
    }

    return validFiles;
  }, [accept, maxSize, maxFiles]);

  const checkForDuplicates = useCallback(async (files: File[]): Promise<{
    duplicates: DuplicateInfo[];
    nonDuplicates: File[];
  }> => {
    const duplicates: DuplicateInfo[] = [];
    const nonDuplicates: File[] = [];

    // Get ALL existing files across all folders (both active and archived)
    // Don't filter by folder so we catch duplicates anywhere
    const [activeResult, archivedResult] = await Promise.all([
      api.listFiles({
        status: 'active',
        // No folderId filter - search all folders
        limit: 1000,
      }),
      api.listFiles({
        status: 'archived',
        // No folderId filter - search all folders
        limit: 1000,
      }),
    ]);

    const allExistingFiles = [...activeResult.files, ...archivedResult.files];
    const existingByName = new Map(
      allExistingFiles.map((f) => [f.fileName.toLowerCase(), f])
    );

    for (const file of files) {
      const existing = existingByName.get(file.name.toLowerCase());
      if (existing) {
        duplicates.push({ file, existingFile: existing });
      } else {
        nonDuplicates.push(file);
      }
    }

    return { duplicates, nonDuplicates };
  }, []);

  const proceedWithUpload = useCallback(async (files: File[]) => {
    if (files.length > 0) {
      completedFilesRef.current = [];
      await uploadFiles(files, { category, folderId });
    }
  }, [uploadFiles, category, folderId]);

  const handleReplaceAndUpload = useCallback(async () => {
    if (!duplicateDialog) return;

    // Delete existing files that are being replaced
    for (const { existingFile } of duplicateDialog.duplicates) {
      await api.deleteFile(existingFile.fileId);
    }

    // Upload all files (duplicates + non-duplicates)
    const allFiles = [
      ...duplicateDialog.duplicates.map((d) => d.file),
      ...duplicateDialog.nonDuplicates,
    ];
    setDuplicateDialog(null);
    await proceedWithUpload(allFiles);
  }, [duplicateDialog, proceedWithUpload]);

  const handleKeepBothAndUpload = useCallback(async () => {
    if (!duplicateDialog) return;

    // Upload all files without deleting existing
    const allFiles = [
      ...duplicateDialog.duplicates.map((d) => d.file),
      ...duplicateDialog.nonDuplicates,
    ];
    setDuplicateDialog(null);
    await proceedWithUpload(allFiles);
  }, [duplicateDialog, proceedWithUpload]);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles = validateFiles(fileArray);

    if (validFiles.length === 0) return;

    // Check for duplicates
    const { duplicates, nonDuplicates } = await checkForDuplicates(validFiles);

    if (duplicates.length > 0) {
      // Show dialog
      setDuplicateDialog({ duplicates, nonDuplicates });
    } else {
      // No duplicates, proceed with upload
      await proceedWithUpload(validFiles);
    }
  }, [validateFiles, checkForDuplicates, proceedWithUpload]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragActive(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, handleFiles]);

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFiles]);

  const hasUploads = uploads.length > 0;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={maxFiles > 1}
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />

        <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-sm font-medium mb-1">
          {isDragActive ? 'Drop files here' : 'Drag and drop files here'}
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          or click to browse
        </p>
        <p className="text-xs text-muted-foreground">
          Max {maxFiles} file{maxFiles !== 1 ? 's' : ''}, up to {formatBytes(maxSize)} each
        </p>
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {validationError}
        </div>
      )}

      {/* Upload progress */}
      {hasUploads && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Uploads</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCompleted}
              disabled={isUploading}
            >
              Clear completed
            </Button>
          </div>

          <div className="space-y-2">
            {uploads.map((upload) => (
              <UploadItem
                key={upload.fileId}
                upload={upload}
                onCancel={() => cancelUpload(upload.fileId)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Duplicate file dialog */}
      <AlertDialog open={duplicateDialog !== null} onOpenChange={(open) => !open && setDuplicateDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>File already exists</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground">
                {duplicateDialog?.duplicates.length === 1 ? (
                  <>
                    A file named <strong>{duplicateDialog.duplicates[0].file.name}</strong> already exists
                    {duplicateDialog.duplicates[0].existingFile.folderId && ' (in a folder)'}
                    {duplicateDialog.duplicates[0].existingFile.status === 'archived' && ' (archived)'}.
                    Do you want to replace it or keep both files?
                  </>
                ) : (
                  <>
                    {duplicateDialog?.duplicates.length} files already exist:
                    <ul className="mt-2 list-disc list-inside">
                      {duplicateDialog?.duplicates.map((d) => (
                        <li key={d.existingFile.fileId}>
                          {d.file.name}
                          {d.existingFile.folderId && <span className="text-muted-foreground/70"> (in a folder)</span>}
                          {d.existingFile.status === 'archived' && <span className="text-muted-foreground/70"> (archived)</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2">Do you want to replace them or keep both versions?</p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'outline' })}
              onClick={handleKeepBothAndUpload}
            >
              Keep Both
            </AlertDialogAction>
            <AlertDialogAction onClick={handleReplaceAndUpload}>
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface UploadItemProps {
  upload: UploadProgress;
  onCancel: () => void;
}

function UploadItem({ upload, onCancel }: UploadItemProps) {
  const { fileName, progress, status, error } = upload;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
      {/* Icon */}
      <div className="flex-shrink-0">
        {status === 'completed' ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : status === 'failed' ? (
          <AlertCircle className="h-5 w-5 text-destructive" />
        ) : status === 'uploading' || status === 'processing' ? (
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
        ) : (
          <File className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{fileName}</p>
        {error ? (
          <p className="text-xs text-destructive">{error}</p>
        ) : status === 'processing' ? (
          <p className="text-xs text-muted-foreground">Processing...</p>
        ) : status === 'completed' ? (
          <p className="text-xs text-muted-foreground">Complete</p>
        ) : status === 'cancelled' ? (
          <p className="text-xs text-muted-foreground">Cancelled</p>
        ) : (
          <div className="mt-1">
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Progress/Cancel */}
      <div className="flex-shrink-0 flex items-center gap-2">
        {(status === 'uploading' || status === 'processing') && (
          <>
            <span className="text-xs text-muted-foreground">{progress}%</span>
            <Button variant="ghost" size="icon" onClick={onCancel}>
              <X className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default FileUpload;
